import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LoadingIndicator from 'components/LoadingIndicator';
import LoginFailed from 'components/LoginFailed';
import { useAuth } from 'context/Auth';
import { useBrand } from 'context/Brand';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useRoutes,
  useSearchParams,
} from 'react-router-dom';
import { getRegistrationReturnTo } from 'utils/registrationNavigation';

import { ErrorBoundary } from '../components/ErrorBoundary';
import routes from './normalRoutes';

const queryClient = new QueryClient();

const Routes: FC = () => {
  const { error: authError, logging, user } = useAuth();
  const { pathname, search, state } = useLocation();
  const navigate = useNavigate();
  const { brandInfo } = useBrand();

  const [searchParams] = useSearchParams();
  const redirect2CouponBox =
    !!searchParams.get('collect') ||
    !!searchParams.get('scode') ||
    !!searchParams.get('pcode');
  const isRegistering =
    pathname.startsWith('/benefit') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/terms') ||
    pathname.startsWith('/privacy');
  const isRedirect = !!searchParams.get('r');
  const isRedirectOnlyMember = !!searchParams.get('redirect');
  const isSuspendedPage = pathname.startsWith('/suspended');

  const productID = useMemo(() => searchParams.get('product'), [searchParams]);

  const element = useRoutes(
    routes(
      {
        menuID: brandInfo?.menu?.[0]?.id,
        isMember: user?.isMember || user?.isNormal,
        isNormal: user?.isNormal,
        registrationReturnTo:
          isRedirect || isRedirectOnlyMember || redirect2CouponBox || productID
            ? undefined
            : getRegistrationReturnTo(state),
      },
      search,
    ),
  );

  useEffect(() => {
    if (isRedirect && !user?.isSuspended) {
      navigate({ pathname: '/redirect', search });
    }
  }, [isRedirect, navigate, search, user?.isSuspended]);

  useEffect(() => {
    if (user?.isSuspended && !isSuspendedPage) {
      navigate({ pathname: '/suspended', search }, { replace: true });
    }
  }, [isSuspendedPage, navigate, search, user?.isSuspended]);

  useEffect(() => {
    if (!user?.isSuspended && isRedirectOnlyMember && user?.isMember && !user?.isNormal) {
      navigate({ pathname: '/redirect', search });
    }
  }, [isRedirectOnlyMember, navigate, search, user]);

  useEffect(() => {
    if (!user?.isSuspended && !isRegistering && redirect2CouponBox) {
      navigate({ pathname: '/byurl', search }, { replace: true });
    }
    if (!user?.isSuspended && !isRegistering && productID) {
      navigate(`/product/${productID}`);
    }
  }, [isRegistering, productID, redirect2CouponBox, user?.isSuspended]);

  useEffect(() => {
    document.getElementById('loading')?.remove();
  }, []);

  if (authError) return <LoginFailed />;

  if (user && !logging)
    return (
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>{element}</ErrorBoundary>
      </QueryClientProvider>
    );

  return LoadingIndicator;
};

export default Routes;
